<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';
import {
  WINDOW_RING_CIRCUMFERENCE,
  WINDOW_RING_RADIUS,
  closesAtParts,
  formatClock,
  formatWindowRemaining,
  isWindowWarning,
  lastIncomingAtMs,
  toEpochMs,
  windowProgressPct,
  windowRingDashOffset,
  windowTone,
} from './windowFormat';
import { translateChannelLabel } from './channelMeta';

const props = defineProps({
  chat: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'close']);
const { t } = useI18n();

const now = ref(Date.now());
let tickId = null;

const expiresAtMs = computed(() =>
  toEpochMs(
    props.chat?.messaging_window_expires_at ??
      props.chat?.messagingWindowExpiresAt
  )
);

const remainingMs = computed(() =>
  expiresAtMs.value == null ? null : expiresAtMs.value - now.value
);

const windowClosed = computed(() => {
  if (props.chat?.can_reply === false) return true;
  return remainingMs.value != null && remainingMs.value <= 0;
});

const show = computed(
  () => expiresAtMs.value != null || props.chat?.can_reply === false
);

const remainingParts = computed(() => formatWindowRemaining(remainingMs.value));
const remainingLabel = computed(() => {
  const parts = remainingParts.value;
  return parts.key === 'HM'
    ? t('CONVERSATION.HEADER.WINDOW_REMAINING_HM', {
        hours: parts.hours,
        minutes: String(parts.minutes).padStart(2, '0'),
      })
    : t('CONVERSATION.HEADER.WINDOW_REMAINING_M', { minutes: parts.minutes });
});

const warning = computed(
  () => !windowClosed.value && isWindowWarning(remainingMs.value)
);
const progress = computed(() => windowProgressPct(remainingMs.value));
const tone = computed(() =>
  windowClosed.value ? 'closed' : windowTone(remainingMs.value)
);
const ringOffset = computed(() => windowRingDashOffset(remainingMs.value));
const ringStrokeClass = computed(() => {
  if (tone.value === 'critical') return 'stroke-n-ruby-9';
  if (tone.value === 'tightening') return 'stroke-n-amber-9';
  if (tone.value === 'closed') return 'stroke-n-amber-9';
  return 'stroke-n-teal-9';
});
const pillToneClass = computed(() => {
  if (tone.value === 'critical') return 'font-bold text-n-ruby-11';
  if (tone.value === 'tightening' || tone.value === 'closed') {
    return 'font-bold text-n-amber-11';
  }
  return 'font-medium text-n-slate-11';
});
const channelName = computed(() => translateChannelLabel(props.inbox, t));

const closesLabel = computed(() => {
  const parts = closesAtParts(expiresAtMs.value, now.value);
  if (!parts) return '';
  const time = `${parts.hh}:${parts.mm}`;
  if (parts.dayKey === 'TODAY') {
    return t('CONVERSATION.HEADER.WINDOW_CLOSES_TODAY', { time });
  }
  return t('CONVERSATION.HEADER.WINDOW_CLOSES_TOMORROW', { time });
});

const lastCustomerLabel = computed(() => {
  const clock = formatClock(lastIncomingAtMs(props.chat));
  if (!clock) return '';
  return t('CONVERSATION.HEADER.WINDOW_LAST_CUSTOMER', {
    time: `${clock.hh}:${clock.mm}`,
  });
});

const fepExpiresAtMs = computed(() =>
  toEpochMs(
    props.chat?.messaging_window_fep_expires_at ??
      props.chat?.messagingWindowFepExpiresAt
  )
);
const fepRemainingMs = computed(() =>
  fepExpiresAtMs.value == null ? null : fepExpiresAtMs.value - now.value
);
const fepActive = computed(
  () => fepRemainingMs.value != null && fepRemainingMs.value > 0
);
const fepLabel = computed(() => {
  if (!fepActive.value) return '';
  const parts = formatWindowRemaining(fepRemainingMs.value);
  const until =
    parts.key === 'HM'
      ? t('CONVERSATION.HEADER.WINDOW_REMAINING_HM', {
          hours: parts.hours,
          minutes: String(parts.minutes).padStart(2, '0'),
        })
      : t('CONVERSATION.HEADER.WINDOW_REMAINING_M', { minutes: parts.minutes });
  return t('CONVERSATION.MESSAGING_WINDOW.FEP_BADGE', { until });
});

const openTemplates = () => {
  emit('close');
  emitter.emit(BUS_EVENTS.OPEN_WHATSAPP_TEMPLATES);
};

onMounted(() => {
  tickId = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  if (tickId) window.clearInterval(tickId);
});
</script>

<template>
  <div class="contents">
    <div v-if="show" class="inline-flex" data-testid="header-window-pill">
      <ChipPopover :open="open" width-class="w-72" @close="emit('close')">
        <template #trigger>
          <button
            v-if="windowClosed"
            type="button"
            class="flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-n-amber-9/20 px-3 text-[12.5px] font-bold text-n-amber-11 hover:bg-n-amber-9/30"
            :title="t('CONVERSATION.HEADER.WINDOW_CLOSED_TIP')"
            @click="emit('toggle')"
          >
            <span class="i-ri-lock-2-line size-4" aria-hidden="true" />
            {{ t('CONVERSATION.MESSAGING_WINDOW.CLOSED') }}
          </button>
          <button
            v-else
            type="button"
            class="flex h-8 items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-1 px-2 text-[12px] tabular-nums hover:bg-n-solid-2"
            :class="pillToneClass"
            :title="
              t('CONVERSATION.HEADER.WINDOW_OPEN_TIP', { when: closesLabel })
            "
            :aria-label="
              t('CONVERSATION.HEADER.WINDOW_OPEN_TIP', { when: closesLabel })
            "
            @click="emit('toggle')"
          >
            <!-- SiteUp: anel SVG que esvazia com o tempo restante (3 faixas). -->
            <svg
              class="size-4 shrink-0 -rotate-90"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <circle
                cx="8"
                cy="8"
                :r="WINDOW_RING_RADIUS"
                class="fill-none stroke-n-solid-3"
                stroke-width="2"
              />
              <circle
                cx="8"
                cy="8"
                :r="WINDOW_RING_RADIUS"
                class="fill-none"
                :class="ringStrokeClass"
                stroke-width="2"
                stroke-linecap="round"
                :stroke-dasharray="WINDOW_RING_CIRCUMFERENCE"
                :stroke-dashoffset="ringOffset"
              />
            </svg>
            {{ remainingLabel }}
          </button>
        </template>

        <template v-if="!windowClosed">
          <b class="block text-[13.5px] font-medium text-n-slate-12">
            {{
              t('CONVERSATION.HEADER.WINDOW_OPEN_TITLE', {
                channel: channelName,
              })
            }}
          </b>
          <p class="m-0 mt-1 text-[12.5px] leading-snug text-n-slate-11">
            <I18nT keypath="CONVERSATION.HEADER.WINDOW_OPEN_BODY" tag="span">
              <template #remaining>
                <b
                  class="font-bold tabular-nums"
                  :class="warning ? 'text-n-amber-11' : 'text-n-slate-12'"
                >
                  {{ remainingLabel }}
                </b>
              </template>
              <template #when>
                {{ closesLabel }}
              </template>
            </I18nT>
          </p>
          <div class="mt-2.5 h-1.5 overflow-hidden rounded-full bg-n-solid-3">
            <div
              class="h-full rounded-full"
              :class="warning ? 'bg-n-amber-9' : 'bg-n-teal-9'"
              :style="{ width: `${progress}%` }"
            />
          </div>
          <p class="m-0 mt-2 text-[11px] leading-snug text-n-slate-10">
            {{ t('CONVERSATION.HEADER.WINDOW_RENEWS') }}
          </p>
          <p
            v-if="fepActive"
            class="m-0 mt-2 text-[11.5px] font-medium text-n-teal-11"
          >
            {{ fepLabel }}
          </p>
        </template>
        <template v-else>
          <b class="block text-[13.5px] font-medium text-n-amber-11">
            {{ t('CONVERSATION.HEADER.WINDOW_CLOSED_TITLE') }}
          </b>
          <p class="m-0 mt-1 text-[12.5px] leading-snug text-n-slate-11">
            <template v-if="lastCustomerLabel">
              {{ lastCustomerLabel }}
            </template>
            {{ t('CONVERSATION.HEADER.WINDOW_CLOSED_BODY') }}
          </p>
          <p
            v-if="fepActive"
            class="m-0 mt-2 text-[11.5px] font-medium text-n-teal-11"
          >
            {{ fepLabel }}
          </p>
          <button
            type="button"
            class="mt-3 flex h-8 w-full items-center justify-center gap-1.5 rounded-full bg-n-blue-9 text-[12.5px] font-bold text-white hover:opacity-90"
            @click="openTemplates"
          >
            <span class="i-ri-file-list-3-line size-4" aria-hidden="true" />
            {{ t('CONVERSATION.HEADER.WINDOW_CHOOSE_TEMPLATE') }}
          </button>
        </template>
      </ChipPopover>
    </div>
  </div>
</template>
